import { Component, OnInit } from "@angular/core"
import { Router } from "@angular/router"
import { SessionService } from "src/app/services/session.service"
import { toastrService } from "src/app/services/ux/toastr.service"
import { Client } from "src/app/services/API/client"
import { LocaleConverterService } from "src/app/common/helpers/locale-converter.service"
import { LayoutService } from "src/app/services/layout.service"
import { TranslateService } from "@ngx-translate/core"
import { LoanEnquireItem, LoanEnquireDoc, Document, LoanEnquireRequest } from "src/app/models/loan-enquire/loan-enquire"
import { SetTitleService } from "src/app/services/set-title.service";


const FILE_SIZE_LIMIT = 10000000
const DOCUMENT_OTHER_TYPE_ID = 4
const fileInputId: string = "file-input"

@Component({
  templateUrl: "./loan-enquire.component.html",
  styleUrls: ["./loan-enquire.component.css"]
})

export class LoanEnquireComponent implements OnInit {
  loading: boolean = false
  items: LoanEnquireItem[] = []
  openingIndexes: number[] = []
  openingItem: LoanEnquireItem | null = null
  docs: LoanEnquireDoc[] = []
  sidebarWidth: number

  constructor(
    public toastr: toastrService,
    private session: SessionService,
    private api: Client,
    private converter: LocaleConverterService,
    private router: Router,
    private layoutService: LayoutService,
    private translateService: TranslateService,
    private settitleservice : SetTitleService
  ) {
    layoutService.sidebarWidth.subscribe(data => this.sidebarWidth = data)
  }

  ngOnInit() {
    this.getData();
    this.settitleservice.sendTitle("Loan Enquires");
  }

  async getData() {
    this.loading = true

    try {
      const data: any = await this.api.get("api/loan/my_loan_enquires")
      this.items = data.sort((item1: any, item2: any) => this.compareTime(item1.createdAt, item2.createdAt))
    } catch(err) {
      this.toastr.error(err.message)
    }

    this.loading = false
  }

  compareTime(time1: string, time2: string) {
    try {
      return new Date(time2).getTime() - new Date(time1).getTime()
    } catch {
      return 0
    }
  }

  goToCreatePage() {
    this.router.navigateByUrl("/create-loan-enquire")
  }

  toggleOpen(index: number) {
    if (this.isOpen(index)) {
      this.openingIndexes = this.openingIndexes.filter(number => number !== index)
      return
    }

    this.openingIndexes = this.openingIndexes.concat(index)
  }

  isOpen(index: number) {
    return this.openingIndexes.includes(index)
  }

  renderDate(date: string) {
    if (!date) return "---"
    return this.converter.formatDate(date)
  }

  setOpeningItem(item: LoanEnquireItem | null) {
    this.openingItem = item
    if (!item) {
      this.docs = []
      return
    }

    this.getDocs(item.loanEnquireId)
  }

  async getDocs(loan_enquire_id: number) {
    this.loading = true

    try {
      const data: any = await this.api.get("api/loan/my_loan_enquire_docs", { loan_enquire_id })
      this.docs = data
    } catch(err) {
      this.toastr.error(err.message)
    }

    this.loading = false
  } 

  async download(DocId: number, fileName: string) {
    this.loading = true

    try {
      const data: any = await this.api.get("api/loan/download_doc", { DocId })
      this.downloadBase64File(data, fileName)
    } catch(err) {
      this.toastr.error(err.message)
    }

    this.loading = false
  }

  downloadBase64File(base64Data: string, fileName: string) {
    const linkSource = `data:application/octet-stream;base64,${base64Data}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource
    downloadLink.download = fileName
    downloadLink.click()
    downloadLink.remove()
  }

  pickDocs() {
    if (!this.openingItem) return
    const input = document.createElement("input")
    input.type = "file"
    input.multiple = true
    input.accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    input.id = fileInputId
    input.classList.add("d-none")
    const label = document.createElement("label")
    label.htmlFor = fileInputId
    document.body.append(input)
    document.body.append(label)
    input.onchange = (e: any) => {
      try {
        e.preventDefault()
        const files = e.target.files

        const enquireDocuments: Document[] = []
        let index: number = 0
        for (const file of files) {
          if (!file.size || file.size >= FILE_SIZE_LIMIT) throw new Error(this.translateService.instant("LoanEnquire.FileOverLimit"))

          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = () => {
            const commaIndex = (reader.result as string).indexOf(",")
            const base64Content = reader.result.slice(commaIndex + 1)
            const newDocuments: Document = {
              docTypeId: DOCUMENT_OTHER_TYPE_ID,
              fileSize: file.size,
              fileName: file.name,
              filePath: file.webkitRelativePath,
              fileBodyString: base64Content as string
            }

            enquireDocuments.push(newDocuments)

            if (index === files.length - 1) {
              this.addDocs(enquireDocuments)
              input.remove()
              label.remove()
            }

            ++index
          }
        }
      } catch(err) {
        this.toastr.error(err.message)
        input.remove()
        label.remove()
      }
    }
    label.click()
  }

  async addDocs(enquireDocuments: Document[]) {
    const { openingItem } = this
    const {
      loanEnquireId,
      purposeId: loanPurposeId,
      amount,
      currencyCode,
      sector,
      subSector,
      sex
    } = openingItem
    this.loading = true

    try {
      if (!enquireDocuments.length) throw new Error(this.translateService.instant("LoanEnquire.NoDocs"))
      const data: LoanEnquireRequest = {
        loanEnquireId,
        loanPurposeId,
        amount,
        currencyCode,
        sector,
        subSector,
        sex,
        enquireDocuments
      }
      try {
        await this.api.post("api/loan/loan_enquire", data)
      } catch(err) {
        if (err) throw err;
      }
      await this.getDocs(loanEnquireId)
      this.toastr.success(this.translateService.instant("LoanEnquire.AddDocSuccess"))
    } catch(err) {
      this.toastr.error(err.message)
    }

    this.loading = false
  }
}